/**
 * Environments API Response Mappers
 *
 * Each function handles a specific API endpoint response format.
 */
import { reverseMap, transformArray, transformKeys } from '@/shared/mappers/base.mapper';

import type {
  Credential,
  Environment,
  EnvironmentOverview,
  FileSchema,
  TestConnectionResponse,
} from '@/types';

// =============================================================================
// Field Mappings (Backend → Frontend)
// =============================================================================

/**
 * Environment field mapping
 * Note: Most environment fields are already camelCase (id, name, color, etc.)
 */
const ENVIRONMENT_FIELDS = {
  // Most fields already camelCase: id, name, color, index, isProd, environmentId
  // Only transform if backend sends PascalCase versions
  Id: 'id',
  Name: 'name',
  Color: 'color',
  Index: 'index',
};

/**
 * EnvironmentOverview field mapping
 * Includes environment + credentials + triggers
 */
const ENVIRONMENT_OVERVIEW_FIELDS = {
  Id: 'id',
  Name: 'name',
  IsProd: 'isProd',
  Credentials: 'credentials',
  Triggers: 'triggers',
  LastExecution: 'lastExecution',
};

/**
 * Credential field mapping
 * Backend uses mixed case for credentials
 */
const CREDENTIAL_FIELDS = {
  Id: 'id',
  Key: 'key',
  Name: 'name',
  ProviderLabel: 'providerLabel',
  CredentialType: 'credentialType',
  Credentials: 'credentials',
  CredentialsJson: 'credentialsJson',
  ConfigJson: 'configJson',
  // Preserve camelCase: credentialType, providerLabel, credentialsJson, key, credentials
};

/**
 * FileSchema field mapping
 * Backend FileResourceDTO returns camelCase: byteSize, uploadedTime, name
 */
const FILE_SCHEMA_FIELDS = {
  Id: 'id',
  Name: 'name',
  ByteSize: 'size',
  byteSize: 'size', // Backend returns camelCase
  SchemaGuid: 'schemaGuid',
  Fields: 'fields',
  LastModified: 'lastModified',
  UploadedTime: 'lastModified',
  uploadedTime: 'lastModified', // Backend returns camelCase
};

/**
 * TestConnectionResponse field mapping
 */
const CONNECTION_TEST_FIELDS = {
  Success: 'success',
  Message: 'message',
  ErrorCode: 'errorCode',
  Details: 'details',
  // Preserve camelCase: success, message
};

// =============================================================================
// Response Transformers (Backend → Frontend)
// =============================================================================

/**
 * Transform GET /environments response
 * Returns array of environments for an app
 */
export const transformGetEnvironmentsResponse = (response: unknown): Environment[] => {
  return transformArray(response, (item) =>
    transformKeys<Environment>(item, ENVIRONMENT_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /environment/{id} response
 * Returns single environment details
 */
export const transformGetEnvironmentResponse = (response: unknown): Environment => {
  return transformKeys<Environment>(response, ENVIRONMENT_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform GET /environment/{id}/overview response
 * Returns environment overview with credentials and metadata
 */
export const transformGetEnvironmentOverviewResponse = (response: unknown): EnvironmentOverview => {
  return transformKeys<EnvironmentOverview>(response, ENVIRONMENT_OVERVIEW_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Default File Storage (LocalFile) credential - always present per environment.
 * Matches dataProtect environmentActions.js getAllCredentialsForEnvironment() lines 348-360:
 * - emptyLocalFile: credentialType, credentials, credentialsJson, providerLabel
 * - Key 'LocalFile' used for saveCredentials (setCsvFields/postCredentials) - canonical key
 */
const DEFAULT_FILE_STORAGE_CREDENTIAL: Credential = {
  key: 'LocalFile',
  credentialType: 'LocalFile',
  providerLabel: 'LocalFile',
  credentials: '',
  credentialsJson: {},
  defaultConnection: true,
  connectionName: 'File Storage',
};

/**
 * Dedupe LocalFile credentials - keeps one (UI-only, no backend deletion)
 */
export const dedupeLocalFileCredentials = (credentials: Credential[]): Credential[] => {
  const nonLocalFile = credentials.filter((c) => c.credentialType !== 'LocalFile');
  const localFileEntries = credentials.filter((c) => c.credentialType === 'LocalFile');

  const firstLocalFile = localFileEntries[0];
  const singleLocalFile: Credential = firstLocalFile
    ? {
        ...DEFAULT_FILE_STORAGE_CREDENTIAL,
        connectionName: firstLocalFile.connectionName ?? 'File Storage',
        defaultConnection: firstLocalFile.defaultConnection ?? true,
      }
    : DEFAULT_FILE_STORAGE_CREDENTIAL;

  return [...nonLocalFile, singleLocalFile];
};

/**
 * Transform raw credentials from API (keys only, no dedupe).
 * Used when we need to detect/clean duplicate LocalFile entries before UI dedupe.
 */
export const transformGetCredentialsResponseNoDedupe = (response: unknown): Credential[] => {
  return transformArray(response, (item) =>
    transformKeys<Credential>(item, CREDENTIAL_FIELDS, {
      parseJson: true, // CredentialsJson needs parsing
      recursive: true,
    })
  );
};

/**
 * Transform GET /credentials response
 * Returns array of credentials for an environment
 *
 * Ensures LocalFile (File Storage) is always present as default connection.
 * Matches dataProtect: LocalFile connector present even if not configured (TC-ENV-010)
 *
 * Deduplication: Backend can return 2 LocalFile credentials when:
 * - User adds File Storage via "Add Source" (key: localfile)
 * - User uploads files (saveCredentials uses key: LocalFile)
 * We keep exactly one LocalFile to avoid duplicate File Storage entries.
 */
export const transformGetCredentialsResponse = (response: unknown): Credential[] => {
  const credentials = transformGetCredentialsResponseNoDedupe(response);
  return dedupeLocalFileCredentials(credentials);
};

/**
 * Transform GET /credential/{key} response
 * Returns single credential details
 */
export const transformGetCredentialResponse = (response: unknown): Credential => {
  return transformKeys<Credential>(response, CREDENTIAL_FIELDS, {
    parseJson: true,
    recursive: true,
  });
};

/**
 * Transform POST /environment response
 * Returns newly created environment
 */
export const transformCreateEnvironmentResponse = (response: unknown): Environment => {
  return transformKeys<Environment>(response, ENVIRONMENT_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform PUT /environment response
 * Returns updated environment
 */
export const transformUpdateEnvironmentResponse = (response: unknown): Environment => {
  return transformKeys<Environment>(response, ENVIRONMENT_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform POST /credentials/save response
 * Returns saved credential
 */
export const transformSaveCredentialsResponse = (response: unknown): Credential => {
  return transformKeys<Credential>(response, CREDENTIAL_FIELDS, {
    parseJson: true,
    recursive: true,
  });
};

/**
 * Transform POST /test-connection response
 * Returns connection test result
 */
export const transformTestConnectionResponse = (response: unknown): TestConnectionResponse => {
  return transformKeys<TestConnectionResponse>(response, CONNECTION_TEST_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform GET /file-schema response
 * Returns array of file schema information
 */
export const transformGetFileSchemaResponse = (response: unknown): FileSchema[] => {
  return transformArray(response, (item) =>
    transformKeys<FileSchema>(item, FILE_SCHEMA_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

// =============================================================================
// Request Transformers (Frontend → Backend)
// =============================================================================

/**
 * Transform POST /environment request body
 */
export const transformCreateEnvironmentRequest = (data: Partial<Environment>): unknown => {
  const reverseFields = reverseMap(ENVIRONMENT_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform PUT /environment request body
 */
export const transformUpdateEnvironmentRequest = (data: Partial<Environment>): unknown => {
  const reverseFields = reverseMap(ENVIRONMENT_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform POST /credentials/save request body
 *
 * IMPORTANT: CredentialDTO struct in Go uses MIXED casing:
 * - Most fields are camelCase: credentialType, credentials, credentialsJson, providerLabel, key, defaultConnection, connectionName
 * - ONE field is PascalCase: ApiAccessible (note lowercase 'i')
 *
 * DO NOT use generic transformKeys here - it will break the payload!
 *
 * Verified against: transformer_api/vendor/github.com/lingkio/data_protect_resources/credentials/credential.go
 */
export const transformSaveCredentialsRequest = (data: Partial<Credential>): unknown => {
  // Build the exact payload structure the backend expects
  const payload: Record<string, unknown> = {};

  // Map camelCase fields directly (no transformation)
  if (data.credentialType !== undefined) payload.credentialType = data.credentialType;
  if (data.credentialsJson !== undefined) payload.credentialsJson = data.credentialsJson;
  if (data.providerLabel !== undefined) payload.providerLabel = data.providerLabel;
  if (data.key !== undefined) payload.key = data.key;
  if (data.connectionName !== undefined) payload.connectionName = data.connectionName;
  if (data.defaultConnection !== undefined) payload.defaultConnection = data.defaultConnection;

  // Special case: ApiAccessible (PascalCase in backend, but our type uses it too)
  if (data.ApiAccessible !== undefined) payload.ApiAccessible = data.ApiAccessible;

  return payload;
};

/**
 * Transform POST /test-connection request body
 *
 * IMPORTANT: The test-connections API expects camelCase keys, exactly as dataProtect sends them.
 * DO NOT transform keys here - the data from ConnectorsList already has the correct casing:
 *   { credentialType, credentialsJson, providerLabel }
 *
 * Verified against production dataProtect curl:
 *   {"credentialType":"SFTP","credentialsJson":{...},"providerLabel":"SFTP"}
 */
export const transformTestConnectionRequest = (data: unknown): unknown => {
  return data;
};

/**
 * Transform POST /env/{envId}/{connectionKey}/getAppAuthCode/{oauthType} request body
 *
 * Backend expects snake_case fields for BVA/Zoom/Canvas OAuth flow.
 * Verified against dataProtect ConnectorSection.js lines 109-140
 */
export const transformBvaOAuthRequest = (data: {
  rootUrl: string;
  clientId: string;
  clientSecret: string;
  scope?: string;
  connectionKey: string;
}): {
  root_url: string;
  client_id: string;
  client_secret: string;
  scope?: string;
  connectionKey: string;
} => {
  return {
    root_url: data.rootUrl,
    client_id: data.clientId,
    client_secret: data.clientSecret,
    scope: data.scope,
    connectionKey: data.connectionKey,
  };
};
